/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { getFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature-Server';
import { getCart } from '@/data/Content/_Cart';
import { getSettings, isB2BStore } from '@/data/Settings-Server';
import { getUser } from '@/data/User-Server';
import { PageDataLookup, pageDataRuntimeFetcher } from '@/data/_PageDataRuntime';
import { getServerCacheScopeForProtectedRoutes } from '@/data/cache/getServerCacheScope';
import { DEFAULT_LANGUAGE, DEFAULT_PAGE_DATA } from '@/data/config/DEFAULTS';
import { PERSISTENT, WCP_PREFIX, WC_PREFIX, WC_SESSION_ESTABLISHED } from '@/data/constants/cookie';
import { DATA_KEY_PAGE_DATA_FROM_ID } from '@/data/constants/dataKey';
import { dataRouteProtectionFlexFlowMap } from '@/data/containers/manifest';
import { Cache } from '@/data/types/Cache';
import { PageDataFromId } from '@/data/types/PageDataFromId';
import { constructPreviewTokenHeaderRequestParams } from '@/data/utils/constructRequestParams';
import { getIdFromPath } from '@/data/utils/getIdFromPath';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { getStaticRoutePageData } from '@/data/utils/getStaticRoutePageData';
import { getTranslationKeyFromPath } from '@/data/utils/getTranslationKeyFromPath';
import { shrink } from '@/data/utils/keyUtil';
import { normalizeStoreTokenPath } from '@/data/utils/normalizeStoreTokenPath';
import { setDefaultLayoutIfNeeded } from '@/data/utils/setDefaultLayoutIfNeeded';
import Cookies from 'cookies';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { unstable_serialize as unstableSerialize } from 'swr';
import { IncomingContent } from './types/IncomingContent';
import { DYNAMIC_CONTENT_PAGE_SEO_TO_FOLDER_MAPPING } from './constants/pageDataFromId';
import { PageIBMStoreDetails } from 'integration/generated/transactions/data-contracts';
import { transactionsPage } from 'integration/generated/transactions';
import { deletePageDataFromCache } from './utils/clearPageLevelCache';
import { getProductDetails } from './Content/ProductDetails';

const DATA_KEY_PAGE_DATA_BY_PAGE_ID = "PAGE_DATA_BY_PAGE_ID";

const OMIT_FOR_KEY = {
	relatedStores: true,
	locationInfo: true,
	contactInfo: true,
	defaultCatalog: true,
	userData: true,
	supportedLanguages: true,
	supportedCurrencies: true,
	storeName: true,
	state: true,
	ownerId: true,
	mapApiKey: true,
	inventorySystem: true,
	currencySymbol: true,
};

const pageFetcher =
	(pub: boolean) =>
	async (
		storeId:string,
		tokenValue:string
	): Promise<PageIBMStoreDetails | undefined> => {
		return transactionsPage(pub).pageFindByPageId(
			storeId,
			tokenValue,
			{
				profileName:"IBM_Store_Details"
			},
			{}
		)
	}

const validateParentPage = async(value:IncomingContent, identifier:string, xCustomField2:string) => {	
	const parentPageIdentifier = identifier.split('/').slice(-2,-1).pop()??'';
	const parentPageFolder = DYNAMIC_CONTENT_PAGE_SEO_TO_FOLDER_MAPPING[parentPageIdentifier];		
	return parentPageFolder?.toLocaleLowerCase()===xCustomField2?.toLocaleLowerCase()?value:undefined;
}

export const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		{ localeId, path, storeId, user, cart, identifier, settings, searchTerm }: PageDataLookup,
		params: RequestParams
	): Promise<PageDataFromId | undefined> => {
		const staticRoutePageData = await getStaticRoutePageData({
			pub,
			path,
			localeId,
			user,
			cart,
			settings,
		});
		if (typeof staticRoutePageData === 'string') {
			// if decided redirect (protected), return redirect;
			const { page, ...other } = DEFAULT_PAGE_DATA;
			return Promise.resolve({ ...other, page: { ...page, redirect: staticRoutePageData } });
		}
		// else try to get from server
		const runtimePageData = await pageDataRuntimeFetcher(pub, context)(
			{ storeId, identifier, searchTerm, staticRoutePageData },
			params
		);
		if (runtimePageData) {
			return runtimePageData;
		}
		if (staticRoutePageData && typeof staticRoutePageData === 'object') {
			return Promise.resolve(staticRoutePageData);
		} else {
			return undefined;
		}
	};

export const getPageDataFromId = async (
	cache: Cache,
	path: ParsedUrlQuery['path'],
	context: GetServerSidePropsContext
) => {
	const settings = await getSettings(cache, context);
	const { storeToken } = settings;
	const { searchTerm } = context.query;
	const identifier = getIdFromPath(path, storeToken);
	const cookie = new Cookies(context.req, context.res);
	const isWcSessionEstablished = !!cookie.get(WC_SESSION_ESTABLISHED);
	const inPreview = settings.inPreview;
	const user = await getUser(cache, context);
	const isHelloRetailSearchEnabled = settings?.userData?.HelloRetailSearchEnabled === "true";


	// Deletes specific page data from the node cache if the query parameter `clear_page_level_cache` is set to 'true'.
	if(context.query?.clear_page_level_cache==='true'){
		deletePageDataFromCache(cache, identifier);
	}
	
	if (user?.sessionError) {
		// This code should NEVER be executed(except for initial page load request),
		// since we will not fetch page props if user session error happened.
		// see pages/[...path].tsx#L49-L50
		// and `docs/cookie-session.md`
		// delete session related cookie on session error
		const cookieKeys = Object.keys(context.req.cookies);
		cookieKeys.forEach((name) => {
			if (inPreview) {
				if (name.startsWith(WCP_PREFIX)) {
					// delete cookie in the response, persistent cookie is useful for remember me case, so keep it
					cookie.set(name, null, { overwrite: true });
				}
			} else if (name.startsWith(WC_PREFIX) && name !== PERSISTENT) {
				// delete cookie in the response, persistent cookie is useful for remember me case, so keep it
				cookie.set(name, null, { overwrite: true });
			}
		});
	}

	const isB2B = isB2BStore(settings);
	const cart = isWcSessionEstablished ? await getCart({ cache, context, id: '' , properties:{calledFrom:"getPageDataFromId"}}) : undefined;
	const havingCart = !!cart?.orderItem; // having cart
	const { storeId, langId } = getServerSideCommon(settings, context);
	const {
		buyerAdmin = false,
		buyerApprover = false,
		sessionError = false,
		isLoggedIn = false,
		isGeneric = false,
	} = user;
	const props = {
		storeId,
		path: normalizeStoreTokenPath({ path, storeUrlKeyword: storeToken?.urlKeywordName }),
		identifier,
		localeId: langId || DEFAULT_LANGUAGE,
		...(searchTerm && { searchTerm: decodeURIComponent([searchTerm].flat().at(0) ?? '') }),
	};
	const params = constructPreviewTokenHeaderRequestParams({ context });
	const key = unstableSerialize([shrink(props, OMIT_FOR_KEY), DATA_KEY_PAGE_DATA_FROM_ID]);

	// these properties are only required at server side to protect routes,
	// once resolved and return to browser means the routes are valid.
	// and hence these should not be part of request cache key used for fallback.
	const serverScopeKey = {
		user: { buyerAdmin, buyerApprover, sessionError, isLoggedIn, isGeneric },
		isB2BStore: isB2B, // seems only isB2B is used by logic to determine page.
		cart: havingCart,
	};
	const cacheScope = getServerCacheScopeForProtectedRoutes({
		serverScopeKey,
		context,
		userContext: user.context,
	});

	const cacheValue = cache.get(key, cacheScope) as unknown as PageDataFromId | undefined;
	let value;

	if (cacheValue) {
		value = cacheValue;
	} else {
		const { localeId, path } = props;
		const { foundEntry } = await getTranslationKeyFromPath({ localeId, path });
		const [route] = foundEntry;
		const id = dataRouteProtectionFlexFlowMap[route as keyof typeof dataRouteProtectionFlexFlowMap];
		const fetch = !id || (await getFlexFlowStoreFeature({ cache, id, context }))?.featureEnabled;
		if (fetch) {
			const response = await fetcher(false)({ ...props, ...serverScopeKey, settings }, params);
			value = response as unknown as PageDataFromId | undefined;
		}
	}
	if(value?.tokenName === 'ProductToken'){
		const product = await getProductDetails({ cache, context, id: value?.tokenExternalValue});
		const show404 = !product?.items || product?.items?.length === 0;  // No items happens if search call fails, and mostly due to published false. Send to 404
		if(show404){
			return undefined;
		} 
	}	

	if(value?.tokenName==="StaticPagesToken"){
		const dataByPageIdKey = unstableSerialize([shrink({storeId,pageId:value?.tokenValue}), DATA_KEY_PAGE_DATA_BY_PAGE_ID]);
		let pageResp;
		const tempPageResp = cache.get(dataByPageIdKey, cacheScope);
		if(tempPageResp){
			pageResp = await tempPageResp;
		}else{
			pageResp = await pageFetcher(false)(storeId, value?.tokenValue);
			cache.set(dataByPageIdKey, pageResp);
		}
		
		const xCustomField2 = pageResp?.resultList?.at(0)?.x_customField2;
		value.x_customField2 = xCustomField2??"";
		if(identifier && identifier.includes("/"))
			value = await validateParentPage(value, identifier, xCustomField2??"");
	}

	if (value && !value.page?.redirect) {
		cache.set(key, value, cacheScope);
	} else {
		cache.set(key, value); // set cache to the same request only if it is redirect or undefined
	}
	return setDefaultLayoutIfNeeded(value, isB2B, {isHelloRetailSearchEnabled}) as PageDataFromId;
};
